import React, {
  useRef, useCallback, useMemo, useState,
} from 'react';
import isEqual from 'lodash/isEqual';
import { useTheme } from '@material-ui/core/styles';

import ConfirmModal from '#web-components/components/ConfirmModal';

import localization from '#web-components/components/Form/localization';
import { FormComponent, FormSubmission } from '../../types';

import './styles.scss';
import FormStyles from '../FormStyles';

import FormMemorized from './FormMemorized';

interface Props {
  language: string,
  onSubmit: (submission: FormSubmission) => void;
  onChange?: (submission: FormSubmission) => void;
  onCustomEvent?: (options: { type: string, data?: Record<string, unknown> }) => void;
  components?: Array<FormComponent>;
  submission?: FormSubmission;
  readOnly?: boolean;
  evalContext?: Record<string, unknown>;
  parentPath?: string;
  fileServiceNonSupport?: boolean;
  formRef?: React.Ref<unknown>;
  onDirtyChange?: (status?: boolean) => void;
  fileMaxSize?: string,
  fileMaxTotalSize?: string,
  blackList?: string[],
}

export function propsComparator(prevProps: Props, nextProps: Props) {
  return isEqual(prevProps, nextProps);
}

export default function FormioForm(props: Props) {
  const { language } = props;
  const localizationKeys = localization[language] as Record<string, string>;
  const theme = useTheme();
  const confirmModalInit = useMemo(() => ({
    confirmationText: '',
    callBack: () => null,
  }), []);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const confirmModal = useRef<{ confirmationText: string, callBack:() => void }>(confirmModalInit);
  const handleClose = useCallback(() => {
    confirmModal.current = confirmModalInit;
    setConfirmModalOpen(false);
  }, [confirmModalInit]);
  const handleOpenConfirm = useCallback((callBack: () => void, confirmationText: string) => {
    confirmModal.current = { confirmationText, callBack };
    setConfirmModalOpen(true);
  }, []);
  const handleConfirm = useCallback(() => {
    setConfirmModalOpen(false);
    confirmModal.current.callBack();
    confirmModal.current = confirmModalInit;
  }, [confirmModalInit]);

  return (
    <div>
      <FormStyles />
      <FormMemorized {...props} theme={theme} handleOpenConfirm={handleOpenConfirm} />
      <ConfirmModal
        title={localizationKeys.confirmModalTitle}
        confirmationText={confirmModal.current.confirmationText}
        isOpen={isConfirmModalOpen}
        submitText={localizationKeys.approve}
        cancelText={localizationKeys.cancel}
        onSubmit={handleConfirm}
        onOpenChange={handleClose}
      />
    </div>
  );
}
